import React, { useState, useEffect } from 'react';
import {
    Shield,
    UserPlus,
    Plus,
    Loader,
    Trash2,
    Database,
    RefreshCw,
    Globe,
    AlertTriangle,
    Users,
    Shuffle,
    UserCheck,
    ChevronDown,
    ChevronUp,
    Zap,
    PhoneCall,
    Mail,
    MessageSquare,
    Bot,
    Bell,
    Search,
    Settings,
    Calendar,
    Edit2,
    CheckCircle,
    Copy,
    ExternalLink,
    Building,
    User,
    Key,
    Eye,
    EyeOff,
    ToggleLeft,
    ToggleRight,
    Lock,
    X
} from 'lucide-react';
import { ROLES, EVENT_TYPES } from '../../lib/constants';
import { userService, settingsService, maintenanceService, sourcesService } from '../../services/api';
import { useLanguage } from '../../contexts/LanguageContext';
import { Skeleton, Dropdown, DropdownTrigger, DropdownPopover, DropdownMenu, DropdownItem } from '../ui';
import ConfirmModal from '../ui/ConfirmModal';

// Simple in-component toast
const useLocalToast = () => {
    const [toast, setToastState] = React.useState(null);
    const setToast = (msg, type = 'success') => {
        setToastState({ msg, type });
        setTimeout(() => setToastState(null), 3500);
    };
    return { toast, setToast };
};

// Helper component for copy button
const CopyButton = ({ text }) => {
    const [copied, setCopied] = useState(false);

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(text);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (err) {
            console.error('Failed to copy:', err);
        }
    };

    return (
        <button
            onClick={handleCopy}
            className={`p-2 rounded-lg transition-colors ${copied ? 'bg-green-100 text-green-600' : 'hover:bg-slate-200 text-slate-600'}`}
            title="Copy Password"
        >
            {copied ? <CheckCircle className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
        </button>
    );
};

export default function AdminPanelView({ activeSection = 'users' }) {
    const { t } = useLanguage();
    const { toast, setToast } = useLocalToast();
    const [users, setUsers] = useState([]);
    const [newUserEmail, setNewUserEmail] = useState('');
    const [newUserName, setNewUserName] = useState('');
    const [newUserRole, setNewUserRole] = useState('Sales');
    const [initialLoading, setInitialLoading] = useState(true);
    const [loading, setLoading] = useState(false);
    const [resetting, setResetting] = useState(false);
    const [assignmentConfig, setAssignmentConfig] = useState({
        mode: 'round_robin',
        defaultAssignee: 'Unassigned',
        sourceRules: []
    });
    const [sources, setSources] = useState([]);
    const [automationRules, setAutomationRules] = useState({});
    const [automationSearch, setAutomationSearch] = useState('');
    const [eventTypes, setEventTypes] = useState([]);
    const [newEventType, setNewEventType] = useState('');
    const [editingEventType, setEditingEventType] = useState(null);
    const [venues, setVenues] = useState([]);
    const [newVenueName, setNewVenueName] = useState('');
    const [newVenueId, setNewVenueId] = useState('');
    const [statusModal, setStatusModal] = useState(null);
    const [confirmDelete, setConfirmDelete] = useState(null);

    // Password management state
    const [passwordModal, setPasswordModal] = useState(null); // { userId, userName, mode: 'view'|'change' }
    const [editUserModal, setEditUserModal] = useState(null);
    const [editUserLoading, setEditUserLoading] = useState(false);
    const [newPassword, setNewPassword] = useState('');
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [passwordLoading, setPasswordLoading] = useState(false);
    const [revealedPassword, setRevealedPassword] = useState(null);
    const [togglingUser, setTogglingUser] = useState(null);
    const [confirmModal, setConfirmModal] = useState(null); // { title, message, confirmText, danger, onConfirm }

    const loadUsers = async () => {
        try {
            const data = await userService.getAll();
            setUsers(data);
        } catch (err) {
            console.error('Error fetching users', err);
        }
    };

    // Load All Initial Data
    useEffect(() => {
        const loadAllData = async () => {
            setInitialLoading(true);
            try {
                // Run loads in parallel
                await Promise.all([
                    // Users
                    loadUsers(),

                    // Sources
                    sourcesService.getAll().then(setSources).catch(err => console.error('Error fetching sources', err)),

                    // Settings
                    (async () => {
                        try {
                            const [assignRules, autoRules, evts] = await Promise.all([
                                settingsService.get('assignment_rules'),
                                settingsService.get('automation_rules'),
                                settingsService.get('event_types')
                            ]);

                            if (assignRules) setAssignmentConfig(assignRules);
                            if (autoRules) setAutomationRules(autoRules);

                            if (evts && evts.list && evts.list.length > 0) {
                                setEventTypes(evts.list);
                            } else {
                                setEventTypes(EVENT_TYPES);
                            }

                            const vens = await settingsService.get('venues');
                            if (vens && vens.list && vens.list.length > 0) {
                                setVenues(vens.list);
                            } else {
                                const defaultVenues = [
                                    { id: 'marquee', name: 'DemoMarquee', isLive: true },
                                    { id: 'banquet', name: 'DemoBanquet Hall', isLive: true }
                                ];
                                setVenues(defaultVenues);
                            }
                        } catch (err) {
                            console.error('Error loading settings', err);
                            setEventTypes(EVENT_TYPES);
                        }
                    })()
                ]);
            } finally {
                setInitialLoading(false);
            }
        };

        loadAllData();
    }, []);

    const handleSaveAssignmentRules = async () => {
        setLoading(true);
        try {
            await settingsService.update('assignment_rules', assignmentConfig);
            setToast(t('assignmentRulesSaved') || 'Assignment rules saved!', 'success');
        } catch (error) {
            console.error(error);
            setToast(t('failedToSaveRules') || 'Failed to save rules.', 'error');
        } finally {
            setLoading(false);
        }
    };

    const addSourceRule = () => {
        setAssignmentConfig((prev) => ({
            ...prev,
            sourceRules: [...(prev.sourceRules || []), { source: '', assignmentType: 'round_robin_role', role: 'Sales', assignTo: '' }]
        }));
    };

    const updateSourceRule = (index, field, value) => {
        setAssignmentConfig((prev) => {
            const rules = [...(prev.sourceRules || [])];
            rules[index] = { ...rules[index], [field]: value };
            return { ...prev, sourceRules: rules };
        });
    };

    const removeSourceRule = (index) => {
        setAssignmentConfig((prev) => ({
            ...prev,
            sourceRules: (prev.sourceRules || []).filter((_, i) => i !== index)
        }));
    };

    const getAutomationRule = (sourceKey) => {
        return automationRules[sourceKey] || {
            addToCallList: true,
            sendNotification: true,
            emailResponse: false,
            textAutoResponse: false,
            aiBot: false
        };
    };

    const toggleAutomation = (sourceKey, field) => {
        setAutomationRules(prev => ({
            ...prev,
            [sourceKey]: {
                ...getAutomationRule(sourceKey),
                [field]: !getAutomationRule(sourceKey)[field]
            }
        }));
    };

    const handleSaveAutomationRules = async () => {
        setLoading(true);
        try {
            await settingsService.update('automation_rules', automationRules);
            setToast(t('automationRulesSaved') || 'Automation rules saved!', 'success');
        } catch (error) {
            console.error(error);
            setToast(t('failedToSaveAutomationRules') || 'Failed to save automation rules.', 'error');
        } finally {
            setLoading(false);
        }
    };

    // Event Type CRUD
    const saveEventTypes = async (newList) => {
        try {
            await settingsService.update('event_types', { list: newList });
            window.dispatchEvent(new CustomEvent('app_settings_updated'));
        } catch (err) {
            console.error('Failed to save event types', err);
            setToast(t('failedToSaveEventTypes') || 'Failed to save event types to server', 'error');
        }
    };

    const handleAddEventType = async () => {
        if (!newEventType.trim()) return;
        const name = newEventType.trim();
        const exists = eventTypes.find(e => (typeof e === 'string' ? e : e.name).toLowerCase() === name.toLowerCase());
        if (exists) {
            setToast(t('eventTypeExists') || 'Event type already exists', 'error');
            return;
        }
        const updated = [...eventTypes, { name, event_type_id: null }];
        setEventTypes(updated);
        await saveEventTypes(updated);
        setNewEventType('');
    };

    const handleUpdateEventType = async (oldVal, newName) => {
        if (!newName.trim()) return;
        const updated = eventTypes.map(e => {
            const eName = typeof e === 'string' ? e : e.name;
            if (eName === oldVal) return typeof e === 'string' ? { name: newName.trim(), event_type_id: null } : { ...e, name: newName.trim() };
            return e;
        });
        setEventTypes(updated);
        await saveEventTypes(updated);
        setEditingEventType(null);
    };

    const handleDeleteEventType = (name) => {
        setConfirmModal({
            title: t('deleteEventTypeConfirm') ? t('deleteEventTypeConfirm').replace('{name}', name) : `Delete event type "${name}"?`,
            message: 'This action cannot be undone.',
            confirmText: 'Delete',
            danger: true,
            onConfirm: async () => {
                const updated = eventTypes.filter(e => (typeof e === 'string' ? e : e.name) !== name);
                setEventTypes(updated);
                await saveEventTypes(updated);
                setConfirmModal(null);
            }
        });
    };

    // Venue CRUD
    const saveVenues = async (newList) => {
        try {
            await settingsService.update('venues', { list: newList });
            window.dispatchEvent(new CustomEvent('app_settings_updated'));
        } catch (err) {
            console.error('Failed to save venues', err);
            setToast(t('failedToSaveVenues') || 'Failed to save venues to server', 'error');
        }
    };

    const handleAddVenue = async () => {
        if (!newVenueName.trim()) return;
        const id = newVenueName.trim().toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
        if (venues.find(v => v.name.toLowerCase() === newVenueName.trim().toLowerCase())) {
            setToast(t('venueExists') || 'Venue already exists', 'error');
            return;
        }
        // If id collision, append a suffix
        const finalId = venues.find(v => v.id === id) ? `${id}_${Date.now()}` : id;
        const newVenue = { id: finalId, name: newVenueName.trim(), isLive: true, venue_id: newVenueId.trim() ? Number(newVenueId.trim()) : null };
        const updated = [...venues, newVenue];
        setVenues(updated);
        await saveVenues(updated);
        setNewVenueName('');
        setNewVenueId('');
    };

    const handleDeleteVenue = (id) => {
        const venue = venues.find(v => v.id === id);
        setConfirmModal({
            title: t('deleteVenueConfirm') || 'Delete this venue?',
            message: venue ? `"${venue.name}" will be permanently removed.` : 'This action cannot be undone.',
            confirmText: 'Delete',
            danger: true,
            onConfirm: async () => {
                const updated = venues.filter(v => v.id !== id);
                setVenues(updated);
                await saveVenues(updated);
                setConfirmModal(null);
            }
        });
    };

    const handleToggleVenueLive = async (id) => {
        const updated = venues.map(v => v.id === id ? { ...v, isLive: !v.isLive } : v);
        setVenues(updated);
        await saveVenues(updated);
    };

    const handleAddUser = async (e) => {
        e.preventDefault();
        if (!newUserEmail.trim() || !newUserName.trim()) return;
        setLoading(true);
        try {
            const result = await userService.invite({
                email: newUserEmail,
                name: newUserName,
                role: newUserRole
            });

            if (result.status) {
                const { tempPassword } = result.data;
                setStatusModal({
                    type: 'success',
                    title: t('userCreatedSuccessfully') || 'User Created Successfully',
                    message: (t('userAddedToTeam') || 'User {name} has been added to the team.').replace('{name}', newUserName),
                    password: tempPassword,
                    link: 'https://crmdemo.inplsoftwares.com',
                    details: t('shareCredentials') || 'Please share these credentials with the user.'
                });

                setNewUserEmail('');
                setNewUserName('');
                loadUsers();
                window.dispatchEvent(new CustomEvent('users_updated'));
            } else {
                throw new Error(result.message || 'Failed to invite user');
            }
        } catch (error) {
            console.error(error);

            let errorMessage = error.message || 'An unknown error occurred.';
            let errorDetails = 'Please check the details and try again.';

            if (errorMessage.includes('Prisma') || errorMessage.includes('invocation') || errorMessage.includes('create()')) {
                if (errorMessage.includes('Unique constraint')) {
                    errorMessage = t('emailAlreadyExists') || 'A user with this email already exists.';
                } else if (errorMessage.includes('argument `role`')) {
                    errorMessage = t('invalidRoleSelected') || 'Invalid role selected.';
                    errorDetails = t('roleNotSupported') || 'The selected role is not supported by the system.';
                } else {
                    errorMessage = t('dbOperationFailed') || 'Database operation failed.';
                    errorDetails = t('internalSystemError') || 'An internal system error occurred. Please contact support.';
                }
            }

            setStatusModal({
                type: 'error',
                title: t('userCreationFailed') || 'User Creation Failed',
                message: errorMessage,
                details: errorDetails
            });
        } finally {
            setLoading(false);
        }
    };

    const handleRemoveUser = (user) => {
        setConfirmDelete({ id: user.id, name: user.username || user.email });
    };

    const confirmDeleteAction = async () => {
        if (!confirmDelete) return;
        try {
            await userService.delete(confirmDelete.id);
            loadUsers();
            window.dispatchEvent(new CustomEvent('users_updated'));
            setConfirmDelete(null);
        } catch (error) {
            console.error(error);
            setConfirmDelete(null);
            setStatusModal({
                type: 'error',
                title: t('deletionFailed') || 'Deletion Failed',
                message: error.message || (t('failedToRemoveUser') || 'Failed to remove user'),
                details: t('tryAgainLater') || 'Please try again later.'
            });
        }
    };

    // Reset password — generates new temp password and returns it
    const handleResetPassword = async (user) => {
        setPasswordLoading(true);
        try {
            const result = await userService.resetPassword(user.id);
            setRevealedPassword({ userId: user.id, password: result.data.tempPassword });
            setPasswordModal({ userId: user.id, userName: user.username || user.email, mode: 'view' });
        } catch (error) {
            console.error(error);
            setToast('Failed to reset password: ' + error.message, 'error');
        } finally {
            setPasswordLoading(false);
        }
    };

    // Change password to a custom one
    const handleChangePassword = async () => {
        if (!newPassword || newPassword.length < 6) {
            setToast('Password must be at least 6 characters', 'error');
            return;
        }
        setPasswordLoading(true);
        try {
            await userService.changePassword(passwordModal.userId, newPassword);
            setRevealedPassword({ userId: passwordModal.userId, password: newPassword });
            setPasswordModal(prev => ({ ...prev, mode: 'view' }));
            setNewPassword('');
            setToast('Password changed successfully', 'success');
        } catch (error) {
            console.error(error);
            setToast('Failed to change password: ' + error.message, 'error');
        } finally {
            setPasswordLoading(false);
        }
    };

    const handleUpdateUserRole = async () => {
        if (!editUserModal) return;
        setEditUserLoading(true);
        try {
            await userService.update({ id: editUserModal.id, role: editUserModal.role });
            setToast('User role updated successfully', 'success');
            setEditUserModal(null);
            await loadUsers();
            window.dispatchEvent(new CustomEvent('users_updated'));
        } catch (error) {
            console.error(error);
            setToast('Failed to update user role: ' + error.message, 'error');
        } finally {
            setEditUserLoading(false);
        }
    };

    // Toggle user active/inactive
    const handleToggleActive = async (user) => {
        setTogglingUser(user.id);
        try {
            const result = await userService.toggleActive(user.id, !user.is_active);
            setUsers(prev => prev.map(u => u.id === user.id ? { ...u, is_active: result.data.is_active } : u));
            window.dispatchEvent(new CustomEvent('users_updated'));
            setToast(`User ${result.data.is_active ? 'activated' : 'deactivated'} successfully`, 'success');
        } catch (error) {
            console.error(error);
            setToast('Failed to update user status: ' + error.message, 'error');
        } finally {
            setTogglingUser(null);
        }
    };

    const handleResetDatabase = () => {
        setConfirmModal({
            title: '⚠️ DANGER: Reset Database',
            message: t('resetDatabaseDanger') || 'This will permanently delete ALL leads. This action CANNOT be undone. Are you absolutely sure?',
            confirmText: 'Yes, Delete All',
            danger: true,
            onConfirm: async () => {
                setConfirmModal(null);
                setResetting(true);
                try {
                    await maintenanceService.resetDatabase();
                    setToast(t('databaseClearedSuccess') || 'Database cleared successfully.', 'success');
                    setTimeout(() => window.location.reload(), 1500);
                } catch (err) {
                    console.error(err);
                    setToast((t('failedToResetDatabase') || 'Failed to reset database: {error}').replace('{error}', err.message), 'error');
                } finally {
                    setResetting(false);
                }
            }
        });
    };

    const handleMigrateContacts = () => {
        setConfirmModal({
            title: 'Migrate Contacts',
            message: t('migrateContactsConfirm2') || 'This will ensure all leads have linked contacts. Continue?',
            confirmText: 'Yes, Migrate',
            danger: false,
            onConfirm: async () => {
                setConfirmModal(null);
                setLoading(true);
                try {
                    const result = await maintenanceService.migrateContacts();
                    setToast(result.message, 'success');
                    setTimeout(() => window.location.reload(), 1500);
                } catch (e) {
                    console.error(e);
                    setToast((t('migrationFailed') || 'Migration failed: {error}').replace('{error}', e.message), 'error');
                } finally {
                    setLoading(false);
                }
            }
        });
    };

    const handleConsolidateSources = async () => {
        setLoading(true);
        try {
            const result = await maintenanceService.consolidateSources();
            setToast(result.message, 'success');
        } catch (e) {
            console.error(e);
            setToast((t('consolidationFailed') || 'Consolidation failed: {error}').replace('{error}', e.message), 'error');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="space-y-4 animate-fade-in relative">

            {/* Toast Banner */}
            {toast && (
                <div className={`fixed bottom-5 right-5 z-[9999] flex items-center gap-3 px-4 py-3 rounded-xl shadow-lg text-sm font-medium transition-all animate-fade-in ${toast.type === 'error'
                    ? 'bg-red-600 text-white'
                    : 'bg-green-600 text-white'
                    }`}>
                    {toast.type === 'error' ? <AlertTriangle className="w-4 h-4 shrink-0" /> : <CheckCircle className="w-4 h-4 shrink-0" />}
                    <span>{toast.msg}</span>
                </div>
            )}

            {statusModal && (
                <div className="fixed inset-0 z-50 bg-white/90 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in">
                    <div className={`bg-white rounded-2xl shadow-2xl border w-full max-w-md overflow-hidden ${statusModal.type === 'error' ? 'border-red-100' : 'border-green-100'}`}>
                        <div className={`p-6 text-center ${statusModal.type === 'error' ? 'bg-red-50' : 'bg-green-50'}`}>
                            <div className={`w-16 h-16 rounded-full mx-auto flex items-center justify-center mb-4 ${statusModal.type === 'error' ? 'bg-red-100 text-red-600' : 'bg-green-100 text-green-600'}`}>
                                {statusModal.type === 'error' ? <AlertTriangle className="w-8 h-8" /> : <CheckCircle className="w-8 h-8" />}
                            </div>
                            <h3 className={`text-xl font-bold mb-2 ${statusModal.type === 'error' ? 'text-red-900' : 'text-green-900'}`}>{statusModal.title}</h3>
                            <p className={`text-sm ${statusModal.type === 'error' ? 'text-red-700' : 'text-green-700'}`}>{statusModal.message}</p>
                        </div>

                        <div className="p-6 space-y-4">
                            {statusModal.type === 'success' && (
                                <div className="space-y-4">
                                    <div className="bg-slate-50 p-4 rounded-xl border border-slate-200">
                                        <label className="block text-xs font-bold text-slate-500 mb-1 uppercase tracking-wider">{t('temporaryPassword') || 'Temporary Password'}</label>
                                        <div className="flex items-center gap-2">
                                            <code className="flex-1 font-mono text-lg font-bold text-slate-800 bg-white px-3 py-2 rounded border border-slate-200">{statusModal.password}</code>
                                            <CopyButton text={statusModal.password} />
                                        </div>
                                    </div>

                                    <div className="bg-blue-50 p-4 rounded-xl border border-blue-100">
                                        <div className="flex gap-3">
                                            <div className="bg-blue-100 p-2 rounded-lg h-fit text-blue-600">
                                                <Globe className="w-5 h-5" />
                                            </div>
                                            <div>
                                                <h4 className="font-bold text-blue-900 text-sm mb-1">{t('loginUrl') || 'Login URL'}</h4>
                                                <a
                                                    href={statusModal.link}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="text-blue-600 hover:text-blue-800 text-xs break-all flex items-center gap-1 font-medium"
                                                >
                                                    {statusModal.link} <ExternalLink className="w-3 h-3" />
                                                </a>
                                                <p className="text-xs text-blue-700/80 mt-2 leading-relaxed">
                                                    {t('loginInstructions') || 'Instructions: Go to the link above, login with the temporary password, and change it immediately.'}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {statusModal.type === 'error' && (
                                <div className="bg-red-50 p-4 rounded-xl border border-red-100 text-sm text-red-800">
                                    {statusModal.details}
                                </div>
                            )}

                            <button
                                onClick={() => setStatusModal(null)}
                                className={`w-full py-3 rounded-xl font-bold transition-all transform active:scale-95 ${statusModal.type === 'error' ? 'bg-red-600 hover:bg-red-700 text-white shadow-lg shadow-red-200' : 'bg-green-600 hover:bg-green-700 text-white shadow-lg shadow-green-200'}`}
                            >
                                {statusModal.type === 'error' ? t('close') || 'Close' : 'Done'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {confirmDelete && (
                <div className="fixed inset-0 z-50 bg-white/90 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in">
                    <div className="bg-white rounded-2xl shadow-2xl border border-red-100 w-full max-w-sm overflow-hidden">
                        <div className="p-6 text-center bg-red-50">
                            <div className="w-16 h-16 rounded-full mx-auto flex items-center justify-center mb-4 bg-red-100 text-red-600">
                                <Trash2 className="w-8 h-8" />
                            </div>
                            <h3 className="text-xl font-bold mb-2 text-red-900">{t('revokeAccessQuestion') || 'Revoke Access?'}</h3>
                            <p className="text-sm text-red-700" dangerouslySetInnerHTML={{ __html: (t('revokeAccessConfirm') || 'Are you sure you want to remove <strong>{name}</strong>? This action cannot be undone.').replace('{name}', confirmDelete.name) }}>
                            </p>
                        </div>
                        <div className="p-4 flex gap-3 bg-white">
                            <button
                                onClick={() => setConfirmDelete(null)}
                                className="flex-1 py-2.5 rounded-lg font-semibold text-gray-600 hover:bg-gray-100 transition-colors"
                            >
                                {t('cancel')}
                            </button>
                            <button
                                onClick={confirmDeleteAction}
                                className="flex-1 py-2.5 rounded-lg font-semibold text-white bg-red-600 hover:bg-red-700 shadow-lg shadow-red-200 transition-all active:scale-95"
                            >
                                {t('confirmRevoke') || 'Confirm Revoke'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Generic Confirm Modal */}
            {confirmModal && (
                <ConfirmModal
                    isOpen={!!confirmModal}
                    title={confirmModal.title}
                    message={confirmModal.message}
                    confirmText={confirmModal.confirmText}
                    danger={confirmModal.danger}
                    onConfirm={confirmModal.onConfirm}
                    onCancel={() => setConfirmModal(null)}
                />
            )}

            {/* Page Header */}
            <div className="mb-4">
                <h2 className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-purple-600 to-indigo-600">
                    {t('adminControlPanel')}
                </h2>
                <p className="text-gray-500 mt-1">{t('manageTeamAccess')}</p>
            </div>

            {/* Invite Team Member */}
            {activeSection === 'users' && (
                <div className="bg-white p-5 rounded-xl shadow-sm border border-gray-200">
                    <h3 className="text-sm font-bold text-gray-800 mb-4 flex items-center gap-2">
                        <UserPlus className="w-4 h-4" /> {t('inviteTeamMember')}
                    </h3>
                    <form onSubmit={handleAddUser} className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        <input
                            className="px-3 py-2 border rounded-lg text-sm"
                            placeholder={t('name')}
                            value={newUserName}
                            onChange={(e) => setNewUserName(e.target.value)}
                            required
                        />
                        <input
                            className="px-3 py-2 border rounded-lg text-sm"
                            placeholder={t('email')}
                            type="email"
                            value={newUserEmail}
                            onChange={(e) => setNewUserEmail(e.target.value)}
                            required
                        />
                        <div className="flex gap-2">
                            <div className="flex-1 min-w-0">
                                <Dropdown>
                                    <DropdownTrigger>
                                        <button type="button" className="w-full px-3 py-2 border rounded-lg text-sm bg-white flex justify-between items-center text-left">
                                            {newUserRole}
                                            <ChevronDown className="w-4 h-4 text-gray-400 shrink-0 ml-2" />
                                        </button>
                                    </DropdownTrigger>
                                    <DropdownPopover className="w-full">
                                        <DropdownMenu
                                            selectedKeys={new Set([newUserRole])}
                                            selectionMode="single"
                                            onSelectionChange={(keys) => setNewUserRole(Array.from(keys)[0])}
                                        >
                                            {ROLES.map((r) => (
                                                <DropdownItem key={r} id={r}>{r}</DropdownItem>
                                            ))}
                                        </DropdownMenu>
                                    </DropdownPopover>
                                </Dropdown>
                            </div>
                            <button
                                type="submit"
                                disabled={loading}
                                className="bg-blue-600 hover:bg-blue-700 text-white p-2 rounded-lg transition-colors"
                            >
                                {loading ? <Loader className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                            </button>
                        </div>
                    </form>
                    <p className="text-xs text-gray-500 mt-2">
                        {t('inviteUserHelperText') || '📧 User will receive a temporary password/link to access the CRM.'}
                    </p>
                </div>
            )}

            {/* Assignment Rules Section */}
            {activeSection === 'assignment' && (
                <div className="bg-purple-50 border border-purple-200 rounded-xl">
                    <div className="w-full p-5 flex items-center justify-between text-left border-b border-purple-200 mb-4">
                        <div className="flex items-center gap-2">
                            <Shuffle className="w-4 h-4 text-purple-600" />
                            <h3 className="text-sm font-bold text-purple-800">{t('assignmentRules')}</h3>
                        </div>
                    </div>

                    <div className="px-5 pb-5 space-y-4">
                        <div>
                            <label className="block text-xs font-bold text-purple-700 mb-2">{t('assignmentMode')}</label>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                {[
                                    { value: 'round_robin', label: t('roundRobin'), desc: t('roundRobinDesc') },
                                    { value: 'source_based', label: t('bySource'), desc: t('bySourceDesc') },
                                    { value: 'single_person', label: t('singlePerson'), desc: t('singlePersonDesc') },
                                    { value: 'manual', label: t('manualOnly'), desc: t('manualOnlyDesc') }
                                ].map((opt) => (
                                    <button
                                        key={opt.value}
                                        onClick={() => setAssignmentConfig((prev) => ({ ...prev, mode: opt.value }))}
                                        className={`p-3 rounded-lg border text-left transition-all ${assignmentConfig.mode === opt.value
                                            ? 'border-purple-500 bg-purple-100'
                                            : 'border-gray-200 bg-white hover:border-purple-300'
                                            }`}
                                    >
                                        <div className="font-medium text-sm">{opt.label}</div>
                                        <div className="text-xs text-gray-500">{opt.desc}</div>
                                    </button>
                                ))}
                            </div>
                        </div>

                        {assignmentConfig.mode === 'single_person' && (
                            <div>
                                <label className="block text-xs font-bold text-purple-700 mb-2">{t('assignAllLeadsTo')}</label>
                                <Dropdown>
                                    <DropdownTrigger>
                                        <button type="button" className="w-full px-3 py-2 border rounded-lg text-sm bg-white flex justify-between items-center text-left">
                                            {assignmentConfig.defaultAssignee ? users.find(u => u.username === assignmentConfig.defaultAssignee)?.username || users.find(u => u.email === assignmentConfig.defaultAssignee)?.email || assignmentConfig.defaultAssignee : 'Select User...'}
                                            <ChevronDown className="w-4 h-4 text-gray-400 shrink-0 ml-2" />
                                        </button>
                                    </DropdownTrigger>
                                    <DropdownPopover className="w-full">
                                        <DropdownMenu
                                            selectedKeys={new Set([assignmentConfig.defaultAssignee || ''])}
                                            selectionMode="single"
                                            onSelectionChange={(keys) => setAssignmentConfig((prev) => ({ ...prev, defaultAssignee: Array.from(keys)[0] }))}
                                        >
                                            <DropdownItem id="">Select User...</DropdownItem>
                                            {users.map((u) => (
                                                <DropdownItem key={u.id} id={u.username || u.email}>{u.username || u.email}</DropdownItem>
                                            ))}
                                        </DropdownMenu>
                                    </DropdownPopover>
                                </Dropdown>
                            </div>
                        )}

                        {assignmentConfig.mode === 'source_based' && (
                            <div>
                                <label className="block text-xs font-bold text-purple-700 mb-2">{t('sourceEmployeeRules')}</label>
                                <div className="space-y-3">
                                    {(assignmentConfig.sourceRules || []).map((rule, idx) => (
                                        <div key={idx} className="p-4 bg-white rounded-xl border border-gray-200 space-y-3">
                                            {/* Row 1: Source + Assignment Type */}
                                            <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
                                                {/* Source Dropdown */}
                                                <div className="flex-1 min-w-0">
                                                    <label className="block text-[10px] font-semibold text-gray-400 uppercase mb-1">{t('sourceLabel') || 'Source'}</label>
                                                    <Dropdown>
                                                        <DropdownTrigger>
                                                            <button type="button" className="w-full px-3 py-2 border rounded-lg text-sm bg-white flex justify-between items-center text-left">
                                                                {rule.source || t('selectSource') || 'Select Source...'}
                                                                <ChevronDown className="w-4 h-4 text-gray-400 shrink-0 ml-2" />
                                                            </button>
                                                        </DropdownTrigger>
                                                        <DropdownPopover className="w-full">
                                                            <DropdownMenu
                                                                selectedKeys={new Set([rule.source])}
                                                                selectionMode="single"
                                                                onSelectionChange={(keys) => updateSourceRule(idx, 'source', Array.from(keys)[0])}
                                                            >
                                                                <DropdownItem id="">{t('selectSource') || 'Select Source...'}</DropdownItem>
                                                                {sources.map((s) => (
                                                                    <DropdownItem key={s.id} id={s.name}>{s.name}</DropdownItem>
                                                                ))}
                                                            </DropdownMenu>
                                                        </DropdownPopover>
                                                    </Dropdown>
                                                </div>
                                                <span className="text-gray-400 hidden sm:block pt-5">→</span>
                                                {/* Assignment Type Dropdown */}
                                                <div className="flex-1 min-w-0">
                                                    <label className="block text-[10px] font-semibold text-gray-400 uppercase mb-1">{t('assignmentTypeLabel') || 'Assignment Type'}</label>
                                                    <Dropdown>
                                                        <DropdownTrigger>
                                                            <button type="button" className="w-full px-3 py-2 border rounded-lg text-sm bg-white flex justify-between items-center text-left">
                                                                {(rule.assignmentType || 'specific_user') === 'round_robin_role' ? <div className="flex items-center gap-2"><RefreshCw className="w-4 h-4 text-blue-500" /> {t('roundRobinSales') || 'Round Robin (Sales)'}</div>
                                                                    : (rule.assignmentType === 'round_robin_all') ? <div className="flex items-center gap-2"><RefreshCw className="w-4 h-4 text-purple-500" /> {t('roundRobinAll') || 'Round Robin (All Users)'}</div>
                                                                        : <div className="flex items-center gap-2"><User className="w-4 h-4 text-gray-500" /> {t('specificUser') || 'Specific User'}</div>}
                                                                <ChevronDown className="w-4 h-4 text-gray-400 shrink-0 ml-2" />
                                                            </button>
                                                        </DropdownTrigger>
                                                        <DropdownPopover className="w-full">
                                                            <DropdownMenu
                                                                selectedKeys={new Set([rule.assignmentType || 'specific_user'])}
                                                                selectionMode="single"
                                                                onSelectionChange={(keys) => {
                                                                    const type = Array.from(keys)[0];
                                                                    setAssignmentConfig((prev) => {
                                                                        const rules = [...(prev.sourceRules || [])];
                                                                        rules[idx] = {
                                                                            ...rules[idx],
                                                                            assignmentType: type,
                                                                            role: type === 'round_robin_role' ? (rules[idx].role || 'Sales') : null,
                                                                            assignTo: type === 'specific_user' ? (rules[idx].assignTo || '') : null
                                                                        };
                                                                        return { ...prev, sourceRules: rules };
                                                                    });
                                                                }}
                                                            >
                                                                <DropdownItem id="round_robin_role"><div className="flex items-center gap-2"><RefreshCw className="w-4 h-4 text-blue-500" /> {t('roundRobinSales') || 'Round Robin (Sales)'}</div></DropdownItem>
                                                                <DropdownItem id="round_robin_all"><div className="flex items-center gap-2"><RefreshCw className="w-4 h-4 text-purple-500" /> {t('roundRobinAll') || 'Round Robin (All Users)'}</div></DropdownItem>
                                                                <DropdownItem id="specific_user"><div className="flex items-center gap-2"><User className="w-4 h-4 text-gray-500" /> {t('specificUser') || 'Specific User'}</div></DropdownItem>
                                                            </DropdownMenu>
                                                        </DropdownPopover>
                                                    </Dropdown>
                                                </div>
                                                <button
                                                    onClick={() => removeSourceRule(idx)}
                                                    className="self-end sm:self-auto sm:pt-5 p-2 text-red-500 hover:bg-red-50 rounded-lg"
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            </div>

                                            {/* Row 2: Conditional — Specific User selector */}
                                            {(rule.assignmentType || 'specific_user') === 'specific_user' && (
                                                <div className="pl-0 sm:pl-2">
                                                    <label className="block text-[10px] font-semibold text-gray-400 uppercase mb-1">{t('assignToLabel') || 'Assign To'}</label>
                                                    <Dropdown>
                                                        <DropdownTrigger>
                                                            <button type="button" className="w-full sm:w-1/2 px-3 py-2 border rounded-lg text-sm bg-white flex justify-between items-center text-left">
                                                                {rule.assignTo ? users.find(u => u.username === rule.assignTo)?.username || users.find(u => u.email === rule.assignTo)?.email || rule.assignTo : t('selectUser') || 'Select User...'}
                                                                <ChevronDown className="w-4 h-4 text-gray-400 shrink-0 ml-2" />
                                                            </button>
                                                        </DropdownTrigger>
                                                        <DropdownPopover className="w-full">
                                                            <DropdownMenu
                                                                selectedKeys={new Set([rule.assignTo || ''])}
                                                                selectionMode="single"
                                                                onSelectionChange={(keys) => updateSourceRule(idx, 'assignTo', Array.from(keys)[0])}
                                                            >
                                                                <DropdownItem id="">{t('selectUser') || 'Select User...'}</DropdownItem>
                                                                {users.map((u) => (
                                                                    <DropdownItem key={u.id} id={u.username || u.email}>{u.username || u.email}</DropdownItem>
                                                                ))}
                                                            </DropdownMenu>
                                                        </DropdownPopover>
                                                    </Dropdown>
                                                </div>
                                            )}

                                            {/* Info badge for round robin modes */}
                                            {(rule.assignmentType === 'round_robin_role') && (
                                                <p className="text-xs text-purple-600 bg-purple-50 px-3 py-1.5 rounded-lg flex items-start gap-1.5">
                                                    <RefreshCw className="w-3.5 h-3.5 mt-0.5 shrink-0" /> <span>{t('roundRobinSalesDesc') || 'Leads will be distributed among all active Sales users in round-robin order'}</span>
                                                </p>
                                            )}
                                            {(rule.assignmentType === 'round_robin_all') && (
                                                <p className="text-xs text-blue-600 bg-blue-50 px-3 py-1.5 rounded-lg flex items-start gap-1.5">
                                                    <RefreshCw className="w-3.5 h-3.5 mt-0.5 shrink-0" /> <span>{t('roundRobinAllDesc') || 'Leads will be distributed among all active users in round-robin order'}</span>
                                                </p>
                                            )}
                                        </div>
                                    ))}
                                    <button
                                        onClick={addSourceRule}
                                        className="text-purple-600 text-sm font-medium flex items-center gap-1 hover:text-purple-700"
                                    >
                                        <Plus className="w-4 h-4" /> {t('addRule') || 'Add Rule'}
                                    </button>
                                </div>
                                <p className="text-xs text-gray-500 mt-2">{t('noSourceRulesDesc') || 'Leads from sources not listed will use round-robin.'}</p>
                            </div>
                        )}

                        {assignmentConfig.mode !== 'manual' && (
                            <div>
                                <label className="block text-xs font-bold text-purple-700 mb-2">{t('fallbackAssignee')}</label>
                                <Dropdown>
                                    <DropdownTrigger>
                                        <button type="button" className="w-full px-3 py-2 border rounded-lg text-sm bg-white flex justify-between items-center text-left">
                                            {(assignmentConfig.fallbackAssignee || 'round_robin') === 'round_robin' ? t('useRoundRobin') :
                                                (assignmentConfig.fallbackAssignee === 'unassigned') ? t('leaveUnassigned') :
                                                    t('alwaysAssignTo').replace('{name}', users.find(u => u.username === assignmentConfig.fallbackAssignee)?.username || users.find(u => u.email === assignmentConfig.fallbackAssignee)?.email || assignmentConfig.fallbackAssignee)}
                                            <ChevronDown className="w-4 h-4 text-gray-400 shrink-0 ml-2" />
                                        </button>
                                    </DropdownTrigger>
                                    <DropdownPopover className="w-full">
                                        <DropdownMenu
                                            selectedKeys={new Set([assignmentConfig.fallbackAssignee || 'round_robin'])}
                                            selectionMode="single"
                                            onSelectionChange={(keys) => setAssignmentConfig((prev) => ({ ...prev, fallbackAssignee: Array.from(keys)[0] }))}
                                        >
                                            <DropdownItem id="round_robin">{t('useRoundRobin')}</DropdownItem>
                                            <DropdownItem id="unassigned">{t('leaveUnassigned')}</DropdownItem>
                                            {users.map((u) => (
                                                <DropdownItem key={u.id} id={u.username || u.email}>
                                                    {t('alwaysAssignTo').replace('{name}', u.username || u.email)}
                                                </DropdownItem>
                                            ))}
                                        </DropdownMenu>
                                    </DropdownPopover>
                                </Dropdown>
                            </div>
                        )}

                        <div className="flex justify-center pt-2">
                            <button
                                onClick={handleSaveAssignmentRules}
                                disabled={loading}
                                className="bg-purple-600 hover:bg-purple-700 text-white px-8 py-2.5 rounded-lg text-sm font-bold flex items-center justify-center gap-2 transition-colors shadow-sm"
                            >
                                {loading ? <Loader className="w-4 h-4 animate-spin" /> : <UserCheck className="w-4 h-4" />}
                                {t('saveAssignmentRules')}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Automation Rules Section */}
            {activeSection === 'automation' && (
                <div className="bg-cyan-50 border border-cyan-200 rounded-xl overflow-hidden">
                    <div className="w-full p-5 flex items-center justify-between text-left border-b border-cyan-200 mb-4">
                        <div className="flex items-center gap-2">
                            <Zap className="w-4 h-4 text-cyan-600" />
                            <h3 className="text-sm font-bold text-cyan-800">{t('leadAutomationRules') || 'Lead Automation Rules'}</h3>
                            <span className="text-xs text-cyan-600 bg-cyan-100 px-2 py-0.5 rounded-full">{t('perSource')}</span>
                        </div>
                    </div>

                    <div className="px-5 pb-5 space-y-4">
                        <div className="relative">
                            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                            <input
                                type="text"
                                placeholder={t('searchSources')}
                                value={automationSearch}
                                onChange={(e) => setAutomationSearch(e.target.value)}
                                className="w-full pl-9 pr-4 py-2 border rounded-lg text-sm"
                            />
                        </div>

                        <div className="bg-white rounded-lg border overflow-hidden">
                            <div className="overflow-x-auto">
                                <table className="w-full text-xs">
                                    <thead className="bg-gray-50 border-b">
                                        <tr>
                                            <th className="px-2 sm:px-4 py-3 text-left font-semibold text-gray-600 min-w-[100px] sm:min-w-[150px]">{t('source')}</th>
                                            <th className="px-1 sm:px-3 py-3 text-center font-semibold text-gray-600">
                                                <div className="flex flex-col items-center gap-1">
                                                    <PhoneCall className="w-4 h-4 text-blue-500" />
                                                    <span className="hidden sm:inline">{t('callListTh') || 'Call List'}</span>
                                                </div>
                                            </th>
                                            <th className="px-1 sm:px-3 py-3 text-center font-semibold text-gray-600">
                                                <div className="flex flex-col items-center gap-1">
                                                    <Bell className="w-4 h-4 text-amber-500" />
                                                    <span className="hidden sm:inline">{t('notify')}</span>
                                                </div>
                                            </th>
                                            <th className="px-1 sm:px-3 py-3 text-center font-semibold text-gray-600">
                                                <div className="flex flex-col items-center gap-1">
                                                    <Mail className="w-4 h-4 text-green-500" />
                                                    <span className="hidden sm:inline">{t('email')}</span>
                                                </div>
                                            </th>
                                            <th className="px-1 sm:px-3 py-3 text-center font-semibold text-gray-600">
                                                <div className="flex flex-col items-center gap-1">
                                                    <MessageSquare className="w-4 h-4 text-purple-500" />
                                                    <span className="hidden sm:inline">{t('text')}</span>
                                                </div>
                                            </th>
                                            <th className="px-1 sm:px-3 py-3 text-center font-semibold text-gray-600">
                                                <div className="flex flex-col items-center gap-1">
                                                    <Bot className="w-4 h-4 text-indigo-500" />
                                                    <span className="hidden sm:inline">{t('aiBot')}</span>
                                                </div>
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y">
                                        <tr className="bg-blue-50/50">
                                            <td className="px-2 sm:px-4 py-2.5">
                                                <div>
                                                    <span className="px-1.5 py-0.5 bg-blue-100 text-blue-700 text-[10px] font-bold rounded">{t('default')}</span>
                                                    <div className="text-gray-400 text-[9px] mt-0.5">{t('allSources') || 'All Sources'}</div>
                                                </div>
                                            </td>
                                            {['addToCallList', 'sendNotification', 'emailResponse', 'textAutoResponse', 'aiBot'].map(field => (
                                                <td key={field} className="px-1 py-2.5 text-center">
                                                    <button
                                                        onClick={() => toggleAutomation('_default', field)}
                                                        className={`w-9 h-5 !min-h-[20px] rounded-full transition-colors duration-200 flex-shrink-0 relative ${getAutomationRule('_default')[field] ? 'bg-blue-500' : 'bg-gray-300'
                                                            }`}
                                                        style={{ minWidth: '36px' }}
                                                    >
                                                        <span
                                                            className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow-sm transition-transform duration-200 ${getAutomationRule('_default')[field] ? 'translate-x-4' : 'translate-x-0'
                                                                }`}
                                                        />
                                                    </button>
                                                </td>
                                            ))}
                                        </tr>

                                        {initialLoading ? (
                                            [1, 2, 3, 4, 5, 6].map(i => (
                                                <tr key={i} className="hover:bg-gray-50">
                                                    <td className="px-2 sm:px-4 py-2.5 border-r border-gray-100">
                                                        <Skeleton width={80} height={14} animation="wave" />
                                                    </td>
                                                    {[1, 2, 3, 4, 5].map(j => (
                                                        <td key={j} className="px-1 py-2.5 text-center">
                                                            <Skeleton variant="rounded" width={36} height={20} animation="wave" className="rounded-full mx-auto" />
                                                        </td>
                                                    ))}
                                                </tr>
                                            ))
                                        ) : (
                                            sources
                                                .filter(s => !automationSearch || s.name.toLowerCase().includes(automationSearch.toLowerCase()))
                                                .map(source => {
                                                    const sourceKey = source.name.replace(/\s+/g, '_').toLowerCase();
                                                    return (
                                                        <tr key={source.id} className="hover:bg-gray-50">
                                                            <td className="px-2 sm:px-4 py-2.5">
                                                                <div>
                                                                    <span className="font-medium text-gray-800 text-[10px] sm:text-xs leading-tight block">{source.name}</span>
                                                                    {source.isIntegration && (
                                                                        <span className="px-1 py-0.5 bg-purple-100 text-purple-600 text-[8px] rounded">{t('integrationBadge') || 'Int.'}</span>
                                                                    )}
                                                                </div>
                                                            </td>
                                                            {['addToCallList', 'sendNotification', 'emailResponse', 'textAutoResponse', 'aiBot'].map(field => (
                                                                <td key={field} className="px-1 py-2.5 text-center">
                                                                    <button
                                                                        onClick={() => toggleAutomation(sourceKey, field)}
                                                                        className={`w-9 h-5 !min-h-[20px] rounded-full transition-colors duration-200 flex-shrink-0 relative ${getAutomationRule(sourceKey)[field] ? 'bg-blue-500' : 'bg-gray-300'
                                                                            }`}
                                                                        style={{ minWidth: '36px' }}
                                                                    >
                                                                        <span
                                                                            className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow-sm transition-transform duration-200 ${getAutomationRule(sourceKey)[field] ? 'translate-x-4' : 'translate-x-0'
                                                                                }`}
                                                                        />
                                                                    </button>
                                                                </td>
                                                            ))}
                                                        </tr>
                                                    );
                                                })
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        <div className="flex flex-wrap gap-4 text-xs text-gray-500">
                            <div className="flex items-center gap-1"><PhoneCall className="w-3 h-3 text-blue-500" /><span>{t('callListTh') || 'Call List'} - {t('callListAutoAdd')}</span></div>
                            <div className="flex items-center gap-1"><Bell className="w-3 h-3 text-amber-500" /><span>{t('notifyDesc')}</span></div>
                            <div className="flex items-center gap-1"><Mail className="w-3 h-3 text-green-500" /><span>{t('emailDesc')}</span></div>
                            <div className="flex items-center gap-1"><MessageSquare className="w-3 h-3 text-purple-500" /><span>{t('textDesc')}</span></div>
                            <div className="flex items-center gap-1"><Bot className="w-3 h-3 text-indigo-500" /><span>{t('aiBotDesc')}</span></div>
                        </div>

                        <div className="flex justify-center pt-2">
                            <button
                                onClick={handleSaveAutomationRules}
                                disabled={loading}
                                className="bg-cyan-600 hover:bg-cyan-700 text-white px-8 py-2.5 rounded-lg text-sm font-bold flex items-center justify-center gap-2 transition-colors shadow-sm"
                            >
                                {loading ? <Loader className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
                                {t('saveAutomationRules')}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Settings Section */}
            {activeSection === 'settings' && (
                <div className="bg-emerald-50 border border-emerald-200 rounded-xl overflow-hidden">
                    <div className="w-full p-5 flex items-center justify-between text-left border-b border-emerald-200 mb-4">
                        <div className="flex items-center gap-2">
                            <Settings className="w-4 h-4 text-emerald-600" />
                            <h3 className="text-sm font-bold text-emerald-800">{t('settings')}</h3>
                            <span className="text-xs text-emerald-600 bg-emerald-100 px-2 py-0.5 rounded-full">{t('managersEventTypes')}</span>
                        </div>
                    </div>

                    <div className="px-5 pb-5 space-y-6">
                        {/* Managers / Sales Team - Hidden temporarily */}
                        {false && (
                            <div>
                                <div className="flex items-center gap-2 mb-3">
                                    <Users className="w-4 h-4 text-emerald-600" />
                                    <h4 className="text-sm font-bold text-emerald-800">{t('managersSalesTeam')}</h4>
                                </div>
                                <div className="bg-white p-4 rounded-lg border text-sm text-gray-600">
                                    <p dangerouslySetInnerHTML={{ __html: t('salesTeamManagedAuto') || 'The sales team is now automatically managed based on the <strong>Invited Users</strong>. To add or remove team members, use the "Invite Team Member" section at the top.' }} />
                                    <div className="mt-3 flex flex-wrap gap-2">
                                        {users.map(u => (
                                            <span key={u.id} className="px-2 py-1 bg-gray-100 rounded text-xs border border-gray-200">
                                                {u.username || u.name}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        )}

                        <div>
                            <div className="flex items-center gap-2 mb-3">
                                <Calendar className="w-4 h-4 text-emerald-600" />
                                <h4 className="text-sm font-bold text-emerald-800">{t('managersEventTypes').split(' & ')[1] || t('eventType') + 's'}</h4>
                            </div>
                            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
                                {/* Mobile Card View */}
                                <div className="block sm:hidden p-4 space-y-3 bg-gray-50/50">
                                    {initialLoading ? (
                                        [1, 2, 3].map(i => (
                                            <div key={i} className="bg-white rounded-lg border border-gray-100 p-4 space-y-2">
                                                <Skeleton width={150} height={20} animation="wave" />
                                            </div>
                                        ))
                                    ) : (
                                        eventTypes.map((eventType) => {
                                            const etName = typeof eventType === 'string' ? eventType : eventType.name;
                                            const etId = typeof eventType === 'string' ? null : eventType.event_type_id;
                                            return (
                                                <div key={etName} className="bg-white rounded-lg border border-gray-200 p-3 shadow-sm">
                                                    <div className="flex items-center justify-between mb-2">
                                                        {editingEventType === etName ? (
                                                            <div className="flex items-center gap-2 flex-1">
                                                                <input
                                                                    type="text"
                                                                    defaultValue={etName}
                                                                    autoFocus
                                                                    onBlur={(e) => handleUpdateEventType(etName, e.target.value)}
                                                                    onKeyDown={(e) => {
                                                                        if (e.key === 'Enter') handleUpdateEventType(etName, e.target.value);
                                                                        if (e.key === 'Escape') setEditingEventType(null);
                                                                    }}
                                                                    className="w-full px-2 py-1 border rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-400"
                                                                />
                                                                <button onClick={() => setEditingEventType(null)} className="p-1 text-gray-400 hover:bg-gray-200 rounded">
                                                                    <X className="w-3.5 h-3.5" />
                                                                </button>
                                                            </div>
                                                        ) : (
                                                            <>
                                                                <span className="text-sm font-bold text-gray-800">{etName}</span>
                                                                <div className="flex gap-1 shrink-0">
                                                                    <button onClick={() => setEditingEventType(etName)} className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-md transition-colors" title={t('edit')}>
                                                                        <Edit2 className="w-4 h-4" />
                                                                    </button>
                                                                    <button onClick={() => handleDeleteEventType(etName)} className="p-1.5 text-red-500 hover:bg-red-50 rounded-md transition-colors" title={t('delete')}>
                                                                        <Trash2 className="w-4 h-4" />
                                                                    </button>
                                                                </div>
                                                            </>
                                                        )}
                                                    </div>
                                                    {editingEventType !== etName && (
                                                        <div className="flex items-center gap-2 mt-2 pt-2 border-t border-gray-100">
                                                            <span className="text-xs font-semibold text-gray-500">{t('idLabel') || 'ID'}:</span>
                                                            <input
                                                                type="number"
                                                                className="w-full px-3 py-1.5 border border-gray-200 rounded-md text-sm focus:border-blue-400 focus:outline-none bg-gray-50"
                                                                placeholder="ID (e.g. 1)"
                                                                value={etId ?? ''}
                                                                onChange={async (e) => {
                                                                    const val = e.target.value.trim();
                                                                    const updated = eventTypes.map(et => {
                                                                        const n = typeof et === 'string' ? et : et.name;
                                                                        if (n === etName) return { name: etName, event_type_id: val ? Number(val) : null };
                                                                        return typeof et === 'string' ? { name: et, event_type_id: null } : et;
                                                                    });
                                                                    setEventTypes(updated);
                                                                    await saveEventTypes(updated);
                                                                }}
                                                            />
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })
                                    )}
                                    <div className="mt-4 border-t border-gray-200 pt-3">
                                        <input
                                            type="text"
                                            placeholder={t('newEventTypePlaceholder') || 'New event type...'}
                                            value={newEventType}
                                            onChange={(e) => setNewEventType(e.target.value)}
                                            onKeyDown={(e) => e.key === 'Enter' && handleAddEventType()}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm mb-2 focus:outline-none focus:border-emerald-500"
                                        />
                                        <button
                                            onClick={handleAddEventType}
                                            className="w-full justify-center px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-bold flex items-center gap-1 transition-colors"
                                        >
                                            <Plus className="w-4 h-4" /> {t('add')}
                                        </button>
                                    </div>
                                </div>

                                {/* Desktop Table View */}
                                <div className="hidden sm:block overflow-x-auto">
                                    <table className="w-full text-sm text-left">
                                        <thead className="bg-gray-50 text-gray-600 font-semibold border-b border-gray-200">
                                            <tr>
                                                <th className="px-5 py-3">{t('eventType') || 'Event Type'}</th>
                                                <th className="px-5 py-3 w-40 text-center">{t('idLabel') || 'ID'}</th>
                                                <th className="px-5 py-3 w-32 text-right">{t('actions') || 'Actions'}</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-100">
                                            {initialLoading ? (
                                                [1, 2, 3].map((i) => (
                                                    <tr key={i} className="hover:bg-gray-50">
                                                        <td className="px-5 py-3"><Skeleton width="60%" height={16} animation="wave" /></td>
                                                        <td className="px-5 py-3 text-center"><Skeleton width={80} height={24} animation="wave" className="mx-auto rounded-md" /></td>
                                                        <td className="px-5 py-3 flex justify-end"><Skeleton width={60} height={24} animation="wave" className="rounded-md" /></td>
                                                    </tr>
                                                ))
                                            ) : eventTypes.length === 0 ? (
                                                <tr>
                                                    <td colSpan="3" className="text-center py-6 text-gray-500">
                                                        {t('noEventTypes') || 'No event types found.'}
                                                    </td>
                                                </tr>
                                            ) : (
                                                eventTypes.map((eventType) => {
                                                    const etName = typeof eventType === 'string' ? eventType : eventType.name;
                                                    const etId = typeof eventType === 'string' ? null : eventType.event_type_id;
                                                    return (
                                                        <tr key={etName} className="hover:bg-gray-50 transition-colors">
                                                            <td className="px-5 py-3 font-medium text-gray-900 w-full max-w-[200px]">
                                                                {editingEventType === etName ? (
                                                                    <div className="flex items-center gap-2">
                                                                        <input
                                                                            type="text"
                                                                            defaultValue={etName}
                                                                            autoFocus
                                                                            onBlur={(e) => handleUpdateEventType(etName, e.target.value)}
                                                                            onKeyDown={(e) => {
                                                                                if (e.key === 'Enter') handleUpdateEventType(etName, e.target.value);
                                                                                if (e.key === 'Escape') setEditingEventType(null);
                                                                            }}
                                                                            className="w-full px-2 py-1 border border-blue-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-100"
                                                                        />
                                                                        <button onClick={() => setEditingEventType(null)} className="p-1 text-gray-400 hover:bg-gray-200 rounded shrink-0">
                                                                            <X className="w-3.5 h-3.5" />
                                                                        </button>
                                                                    </div>
                                                                ) : (
                                                                    <div className="flex items-center gap-2">
                                                                        <div className="w-2 h-2 rounded-full bg-emerald-500 flex-shrink-0" />
                                                                        {etName}
                                                                    </div>
                                                                )}
                                                            </td>
                                                            <td className="px-5 py-3 text-center">
                                                                {editingEventType !== etName && (
                                                                    <input
                                                                        type="number"
                                                                        className="w-24 px-2 py-1 border border-gray-200 rounded-lg text-sm text-center focus:border-blue-400 focus:outline-none bg-white transition-colors hover:border-gray-300"
                                                                        placeholder="—"
                                                                        value={etId ?? ''}
                                                                        onChange={async (e) => {
                                                                            const val = e.target.value.trim();
                                                                            const updated = eventTypes.map(et => {
                                                                                const n = typeof et === 'string' ? et : et.name;
                                                                                if (n === etName) return { name: etName, event_type_id: val ? Number(val) : null };
                                                                                return typeof et === 'string' ? { name: et, event_type_id: null } : et;
                                                                            });
                                                                            setEventTypes(updated);
                                                                            await saveEventTypes(updated);
                                                                        }}
                                                                    />
                                                                )}
                                                            </td>
                                                            <td className="px-5 py-3">
                                                                <div className="flex items-center justify-end gap-1">
                                                                    <button onClick={() => setEditingEventType(etName)} className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors" title={t('edit')}>
                                                                        <Edit2 className="w-4 h-4" />
                                                                    </button>
                                                                    <button onClick={() => handleDeleteEventType(etName)} className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg transition-colors" title={t('delete')}>
                                                                        <Trash2 className="w-4 h-4" />
                                                                    </button>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    );
                                                })
                                            )}
                                        </tbody>
                                    </table>
                                    <div className="p-4 bg-gray-50 border-t border-gray-200 flex items-center justify-end gap-3">
                                        <input
                                            type="text"
                                            placeholder={t('newEventTypePlaceholder') || 'New event type...'}
                                            value={newEventType}
                                            onChange={(e) => setNewEventType(e.target.value)}
                                            onKeyDown={(e) => e.key === 'Enter' && handleAddEventType()}
                                            className="w-48 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-emerald-500 shadow-sm"
                                        />
                                        <button
                                            onClick={handleAddEventType}
                                            className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-bold flex items-center gap-1 shrink-0 transition-colors shadow-sm"
                                        >
                                            <Plus className="w-4 h-4" /> {t('add')}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Venue Management */}
                        <div>
                            <div className="flex items-center gap-2 mb-3">
                                <Building className="w-4 h-4 text-emerald-600" />
                                <h4 className="text-sm font-bold text-emerald-800">{t('venues') || 'Venues'}</h4>
                            </div>
                            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
                                {/* Mobile Card View */}
                                <div className="block sm:hidden p-4 space-y-3 bg-gray-50/50">
                                    {initialLoading ? (
                                        [1, 2].map(i => (
                                            <div key={i} className="bg-white rounded-lg border border-gray-100 p-4 space-y-3">
                                                <Skeleton width={150} height={20} animation="wave" />
                                                <Skeleton variant="rounded" width="100%" height={32} animation="wave" className="rounded-md" />
                                            </div>
                                        ))
                                    ) : (
                                        venues.map((venue) => (
                                            <div key={venue.id} className="bg-white rounded-lg border border-gray-200 p-3 shadow-sm">
                                                <div className="flex items-center justify-between mb-2 pb-2 border-b border-gray-100">
                                                    <div className="flex items-center gap-2">
                                                        <span className="text-sm font-bold text-gray-800">{venue.name}</span>
                                                        <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${venue.isLive ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'
                                                            }`}>
                                                            {venue.isLive ? (t('live') || 'Live') : (t('notLive') || 'Not Live')}
                                                        </span>
                                                    </div>
                                                    <div className="flex items-center gap-2 shrink-0">
                                                        <button
                                                            onClick={() => handleToggleVenueLive(venue.id)}
                                                            title={venue.isLive ? (t('disableVenue') || 'Disable') : (t('enableVenue') || 'Enable')}
                                                            className={`relative w-9 h-5 !min-h-[20px] rounded-full transition-colors focus:outline-none flex-shrink-0 ${venue.isLive ? 'bg-emerald-500' : 'bg-gray-300'
                                                                }`}
                                                            style={{ minWidth: '36px' }}
                                                        >
                                                            <span
                                                                className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform duration-200 ${venue.isLive ? 'translate-x-4' : 'translate-x-0'
                                                                    }`}
                                                            />
                                                        </button>
                                                        <button
                                                            onClick={() => handleDeleteVenue(venue.id)}
                                                            className="p-1.5 text-red-500 hover:bg-red-50 rounded-md transition-colors"
                                                        >
                                                            <Trash2 className="w-4 h-4" />
                                                        </button>
                                                    </div>
                                                </div>
                                                <div className="flex items-center justify-between mt-2">
                                                    <span className="text-xs font-semibold text-gray-500">{t('banquetId') || 'Banquet ID'}:</span>
                                                    <input
                                                        type="number"
                                                        className="w-24 px-3 py-1.5 border border-gray-200 rounded-md text-sm text-center focus:border-emerald-400 focus:outline-none bg-gray-50"
                                                        placeholder="ID"
                                                        value={venue.venue_id ?? ''}
                                                        onChange={async (e) => {
                                                            const val = e.target.value.trim();
                                                            const updated = venues.map(v => v.id === venue.id ? { ...v, venue_id: val ? Number(val) : null } : v);
                                                            setVenues(updated);
                                                            await saveVenues(updated);
                                                        }}
                                                    />
                                                </div>
                                            </div>
                                        ))
                                    )}
                                    <div className="mt-4 border-t border-gray-200 pt-3 space-y-2">
                                        <input
                                            type="text"
                                            placeholder={t('newVenueNamePlaceholder') || 'New venue name...'}
                                            value={newVenueName}
                                            onChange={(e) => setNewVenueName(e.target.value)}
                                            onKeyDown={(e) => e.key === 'Enter' && handleAddVenue()}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-emerald-500"
                                        />
                                        <input
                                            type="number"
                                            placeholder="Banquet ID"
                                            value={newVenueId}
                                            onChange={(e) => setNewVenueId(e.target.value)}
                                            onKeyDown={(e) => e.key === 'Enter' && handleAddVenue()}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-emerald-500"
                                        />
                                        <button
                                            onClick={handleAddVenue}
                                            className="w-full justify-center px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-bold flex items-center gap-1 shadow-sm transition-colors"
                                        >
                                            <Plus className="w-4 h-4" /> {t('add')}
                                        </button>
                                    </div>
                                </div>

                                {/* Desktop Table View */}
                                <div className="hidden sm:block overflow-x-auto">
                                    <table className="w-full text-sm text-left">
                                        <thead className="bg-gray-50 text-gray-600 font-semibold border-b border-gray-200">
                                            <tr>
                                                <th className="px-5 py-3">{t('venueName') || 'Venue Name'}</th>
                                                <th className="px-5 py-3 w-40 text-center">{t('banquetId') || 'Banquet ID'}</th>
                                                <th className="px-5 py-3 w-24 text-center">{t('status') || 'Status'}</th>
                                                <th className="px-5 py-3 w-32 text-right">{t('actions') || 'Actions'}</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-100">
                                            {initialLoading ? (
                                                [1, 2].map(i => (
                                                    <tr key={i} className="hover:bg-gray-50">
                                                        <td className="px-5 py-3"><Skeleton width="60%" height={16} animation="wave" /></td>
                                                        <td className="px-5 py-3 text-center"><Skeleton width={80} height={24} animation="wave" className="mx-auto rounded-md" /></td>
                                                        <td className="px-5 py-3 text-center"><Skeleton width={60} height={20} animation="wave" className="mx-auto rounded-full" /></td>
                                                        <td className="px-5 py-3 flex justify-end"><Skeleton width={60} height={24} animation="wave" className="rounded-md" /></td>
                                                    </tr>
                                                ))
                                            ) : venues.length === 0 ? (
                                                <tr>
                                                    <td colSpan="4" className="text-center py-6 text-gray-500">
                                                        {t('noVenuesFound') || 'No venues found.'}
                                                    </td>
                                                </tr>
                                            ) : (
                                                venues.map((venue) => (
                                                    <tr key={venue.id} className="hover:bg-gray-50 transition-colors">
                                                        <td className="px-5 py-3 font-medium text-gray-900 w-full max-w-[200px]">
                                                            <div className="flex items-center gap-2">
                                                                <div className={`w-2 h-2 rounded-full flex-shrink-0 ${venue.isLive ? 'bg-emerald-500' : 'bg-gray-300'}`} />
                                                                {venue.name}
                                                            </div>
                                                        </td>
                                                        <td className="px-5 py-3 text-center">
                                                            <input
                                                                type="number"
                                                                className="w-24 px-2 py-1 border border-gray-200 rounded-lg text-sm text-center focus:border-emerald-400 focus:outline-none bg-white transition-colors hover:border-gray-300"
                                                                placeholder="—"
                                                                value={venue.venue_id ?? ''}
                                                                onChange={async (e) => {
                                                                    const val = e.target.value.trim();
                                                                    const updated = venues.map(v => v.id === venue.id ? { ...v, venue_id: val ? Number(val) : null } : v);
                                                                    setVenues(updated);
                                                                    await saveVenues(updated);
                                                                }}
                                                            />
                                                        </td>
                                                        <td className="px-5 py-3 text-center">
                                                            <span className={`text-[10px] font-bold px-2.5 py-1 rounded-full ${venue.isLive ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'
                                                                }`}>
                                                                {venue.isLive ? (t('live') || 'Live') : (t('notLive') || 'Not Live')}
                                                            </span>
                                                        </td>
                                                        <td className="px-5 py-3">
                                                            <div className="flex items-center justify-end gap-2">
                                                                <button
                                                                    onClick={() => handleToggleVenueLive(venue.id)}
                                                                    title={venue.isLive ? (t('disableVenue') || 'Disable') : (t('enableVenue') || 'Enable')}
                                                                    className={`relative w-9 h-5 !min-h-[20px] rounded-full transition-colors focus:outline-none flex-shrink-0 ${venue.isLive ? 'bg-emerald-500' : 'bg-gray-300'
                                                                        }`}
                                                                    style={{ minWidth: '36px' }}
                                                                >
                                                                    <span
                                                                        className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform duration-200 ${venue.isLive ? 'translate-x-4' : 'translate-x-0'
                                                                            }`}
                                                                    />
                                                                </button>
                                                                <button
                                                                    onClick={() => handleDeleteVenue(venue.id)}
                                                                    className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                                                                >
                                                                    <Trash2 className="w-4 h-4" />
                                                                </button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))
                                            )}
                                        </tbody>
                                    </table>
                                    <div className="p-4 bg-gray-50 border-t border-gray-200 flex items-center justify-end gap-3">
                                        <input
                                            type="text"
                                            placeholder={t('newVenueNamePlaceholder') || 'New venue name...'}
                                            value={newVenueName}
                                            onChange={(e) => setNewVenueName(e.target.value)}
                                            onKeyDown={(e) => e.key === 'Enter' && handleAddVenue()}
                                            className="w-48 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-emerald-500 shadow-sm"
                                        />
                                        <input
                                            type="number"
                                            placeholder="Banquet ID"
                                            value={newVenueId}
                                            onChange={(e) => setNewVenueId(e.target.value)}
                                            onKeyDown={(e) => e.key === 'Enter' && handleAddVenue()}
                                            className="w-32 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-emerald-500 shadow-sm"
                                        />
                                        <button
                                            onClick={handleAddVenue}
                                            className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-bold flex items-center gap-1 shrink-0 transition-colors shadow-sm"
                                        >
                                            <Plus className="w-4 h-4" /> {t('add')}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Team Members Table */}
            {activeSection === 'users' && (
                <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
                    <div className="px-4 sm:px-5 py-4 border-b bg-gray-50 flex items-center gap-2">
                        <Users className="w-4 h-4 text-slate-600" />
                        <h3 className="text-sm font-bold text-gray-800">Team Members</h3>
                        <span className="ml-auto text-xs text-gray-400">{users.length} members</span>
                    </div>

                    {/* Mobile Card View */}
                    <div className="block sm:hidden p-4 space-y-3">
                        {initialLoading ? (
                            [1, 2, 3].map((i) => (
                                <div key={i} className="bg-gray-50 rounded-lg border border-gray-100 p-4 space-y-2">
                                    <Skeleton width={140} height={16} animation="wave" />
                                    <Skeleton width={200} height={13} animation="wave" />
                                    <div className="flex gap-2">
                                        <Skeleton variant="rounded" width={55} height={24} animation="wave" className="rounded-full" />
                                        <Skeleton variant="rounded" width={65} height={24} animation="wave" className="rounded-full" />
                                    </div>
                                </div>
                            ))
                        ) : users.length === 0 ? (
                            <div className="text-center py-8 text-gray-500 text-sm">
                                {t('noTeamMembers') || 'No team members found.'}
                            </div>
                        ) : (
                            users.map((u) => (
                                <div key={u.id} className={`bg-gray-50 rounded-lg border border-gray-100 p-4 ${!u.is_active ? 'opacity-60' : ''}`}>
                                    <div className="flex items-start justify-between gap-2 mb-3">
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 mb-1">
                                                <span className="font-semibold text-gray-900 text-sm truncate">{u.username || u.name}</span>
                                                <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${u.role === 'Admin' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}>
                                                    {u.role}
                                                </span>
                                            </div>
                                            <p className="text-xs text-gray-500 truncate">{u.email}</p>
                                        </div>
                                        <div className="flex items-center gap-2 shrink-0">
                                            <button
                                                onClick={() => handleToggleActive(u)}
                                                disabled={togglingUser === u.id}
                                                className={`relative w-9 h-5 !min-h-[20px] rounded-full transition-colors duration-200 flex-shrink-0 disabled:opacity-50 ${u.is_active ? 'bg-emerald-500' : 'bg-gray-300'
                                                    }`}
                                                style={{ minWidth: '36px' }}
                                            >
                                                {togglingUser === u.id ? (
                                                    <Loader className="absolute top-[2px] left-[2px] w-4 h-4 text-white animate-spin" />
                                                ) : (
                                                    <span
                                                        className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow-sm transition-transform duration-200 ${u.is_active ? 'translate-x-4' : 'translate-x-0'
                                                            }`}
                                                    />
                                                )}
                                            </button>
                                            <button
                                                onClick={() => handleRemoveUser(u)}
                                                className="p-1.5 text-red-400 hover:bg-red-50 rounded-md transition-colors"
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        </div>
                                    </div>
                                    <div className="flex flex-wrap gap-2 pt-3 border-t border-gray-200">
                                        <button
                                            onClick={() => setEditUserModal(u)}
                                            className="inline-flex flex-1 justify-center items-center gap-1.5 px-3 py-1.5 bg-purple-50 hover:bg-purple-100 text-purple-700 rounded-lg text-xs font-medium transition-colors"
                                        >
                                            <Edit2 className="w-3 h-3" />
                                            Edit Role
                                        </button>
                                        <button
                                            onClick={() => handleResetPassword(u)}
                                            disabled={passwordLoading}
                                            className="inline-flex flex-1 justify-center items-center gap-1.5 px-3 py-1.5 bg-amber-50 hover:bg-amber-100 text-amber-700 rounded-lg text-xs font-medium transition-colors"
                                        >
                                            {passwordLoading ? <Loader className="w-3 h-3 animate-spin" /> : <Key className="w-3 h-3" />}
                                            Reset
                                        </button>
                                        <button
                                            onClick={() => { setPasswordModal({ userId: u.id, userName: u.username || u.email, mode: 'change' }); setNewPassword(''); setRevealedPassword(null); }}
                                            className="inline-flex flex-1 justify-center items-center gap-1.5 px-3 py-1.5 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-lg text-xs font-medium transition-colors"
                                        >
                                            <Lock className="w-3 h-3" />
                                            Change
                                        </button>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>

                    {/* Desktop Table View */}
                    <div className="hidden sm:block overflow-x-auto">
                        <table className="w-full text-sm text-left">
                            <thead className="bg-gray-50 text-gray-500 font-medium border-b border-gray-200">
                                <tr>
                                    <th className="px-5 py-3">{t('name')}</th>
                                    <th className="px-5 py-3">{t('email')}</th>
                                    <th className="px-5 py-3">{t('roleLabel') || 'Role'}</th>
                                    <th className="px-5 py-3 text-center">{t('status') || 'Status'}</th>
                                    <th className="px-5 py-3 text-center">{t('password') || 'Password'}</th>
                                    <th className="px-5 py-3 text-right">{t('actions')}</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {initialLoading ? (
                                    [1, 2, 3, 4, 5].map((i) => (
                                        <tr key={i} className="hover:bg-gray-50">
                                            <td className="px-5 py-3"><Skeleton width={120} height={16} animation="wave" /></td>
                                            <td className="px-5 py-3"><Skeleton width={180} height={16} animation="wave" /></td>
                                            <td className="px-5 py-3"><Skeleton variant="rounded" width={60} height={24} animation="wave" className="rounded-full" /></td>
                                            <td className="px-5 py-3"><Skeleton width={70} height={28} animation="wave" className="rounded-full mx-auto" /></td>
                                            <td className="px-5 py-3"><Skeleton width={120} height={28} animation="wave" className="rounded-lg mx-auto" /></td>
                                            <td className="px-5 py-3 flex justify-end"><Skeleton width={24} height={24} animation="wave" /></td>
                                        </tr>
                                    ))
                                ) : users.length === 0 ? (
                                    <tr>
                                        <td colSpan="6" className="text-center py-8 text-gray-500">
                                            {t('noTeamMembers') || 'No team members found.'}
                                        </td>
                                    </tr>
                                ) : (
                                    users.map((u) => (
                                        <tr key={u.id} className={`hover:bg-gray-50 transition-colors ${!u.is_active ? 'opacity-60' : ''}`}>
                                            <td className="px-5 py-3 font-medium text-gray-900">
                                                <div className="flex items-center gap-2">
                                                    <div className={`w-2 h-2 rounded-full flex-shrink-0 ${u.is_active ? 'bg-green-500' : 'bg-gray-300'}`} />
                                                    {u.username || u.name}
                                                </div>
                                            </td>
                                            <td className="px-5 py-3 text-gray-500 text-xs">{u.email}</td>
                                            <td className="px-5 py-3">
                                                <span className={`px-2 py-1 rounded-full text-xs font-bold inline-block whitespace-nowrap ${u.role === 'Admin' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}>
                                                    {u.role}
                                                </span>
                                            </td>
                                            <td className="px-5 py-3 text-center">
                                                <div className="flex items-center justify-center gap-2">
                                                    <span className={`text-[10px] font-bold ${u.is_active ? 'text-emerald-600' : 'text-gray-400'}`}>
                                                        {u.is_active ? 'Active' : 'Inactive'}
                                                    </span>
                                                    <button
                                                        onClick={() => handleToggleActive(u)}
                                                        disabled={togglingUser === u.id}
                                                        className={`relative w-9 h-5 !min-h-[20px] rounded-full transition-colors duration-200 flex-shrink-0 disabled:opacity-50 ${u.is_active ? 'bg-emerald-500' : 'bg-gray-300'
                                                            }`}
                                                        style={{ minWidth: '36px' }}
                                                    >
                                                        {togglingUser === u.id ? (
                                                            <Loader className="absolute top-[2px] left-[2px] w-4 h-4 text-white animate-spin" />
                                                        ) : (
                                                            <span
                                                                className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow-sm transition-transform duration-200 ${u.is_active ? 'translate-x-4' : 'translate-x-0'
                                                                    }`}
                                                            />
                                                        )}
                                                    </button>
                                                </div>
                                            </td>
                                            <td className="px-5 py-3 text-center">
                                                <div className="flex items-center justify-center gap-1">
                                                    <button
                                                        onClick={() => handleResetPassword(u)}
                                                        disabled={passwordLoading}
                                                        title="Reset & View Password"
                                                        className="inline-flex items-center gap-1 px-2.5 py-1.5 bg-amber-50 hover:bg-amber-100 text-amber-700 rounded-lg text-xs font-medium transition-colors"
                                                    >
                                                        {passwordLoading ? <Loader className="w-3 h-3 animate-spin" /> : <Key className="w-3 h-3" />}
                                                        Reset
                                                    </button>
                                                    <button
                                                        onClick={() => { setPasswordModal({ userId: u.id, userName: u.username || u.email, mode: 'change' }); setNewPassword(''); setRevealedPassword(null); }}
                                                        title="Set Custom Password"
                                                        className="inline-flex items-center gap-1 px-2.5 py-1.5 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-lg text-xs font-medium transition-colors"
                                                    >
                                                        <Lock className="w-3 h-3" />
                                                        Change
                                                    </button>
                                                </div>
                                            </td>
                                            <td className="px-5 py-3 text-right">
                                                <div className="flex items-center justify-end gap-1">
                                                    <button
                                                        onClick={() => setEditUserModal(u)}
                                                        className="p-1.5 text-purple-500 hover:bg-purple-50 rounded-md transition-colors"
                                                        title="Edit Role"
                                                    >
                                                        <Edit2 className="w-4 h-4" />
                                                    </button>
                                                    <button
                                                        onClick={() => handleRemoveUser(u)}
                                                        className="p-1.5 text-red-500 hover:bg-red-50 rounded-md transition-colors"
                                                        title={t('revokeAccessQuestion') || 'Revoke Access'}
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Password Modal */}
                    {passwordModal && (
                        <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4">
                            <div className="bg-white rounded-2xl shadow-2xl border border-blue-100 w-full max-w-sm overflow-hidden">
                                <div className="p-5 bg-blue-50 border-b border-blue-100 flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center">
                                            <Key className="w-5 h-5" />
                                        </div>
                                        <div>
                                            <h3 className="font-bold text-blue-900 text-sm">
                                                {passwordModal.mode === 'view' ? 'Password Reset' : 'Change Password'}
                                            </h3>
                                            <p className="text-xs text-blue-600">{passwordModal.userName}</p>
                                        </div>
                                    </div>
                                    <button onClick={() => { setPasswordModal(null); setRevealedPassword(null); setNewPassword(''); }} className="p-1.5 hover:bg-blue-100 rounded-lg">
                                        <X className="w-4 h-4 text-blue-600" />
                                    </button>
                                </div>

                                <div className="p-5 space-y-4">
                                    {/* View mode — show reset password */}
                                    {passwordModal.mode === 'view' && revealedPassword && (
                                        <div>
                                            <p className="text-xs text-gray-500 mb-3">
                                                Password has been reset. Share this temporary password with the user — they will be prompted to change it on next login.
                                            </p>
                                            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                                                <label className="block text-xs font-bold text-amber-700 mb-2 uppercase tracking-wider">New Temporary Password</label>
                                                <div className="flex items-center gap-2">
                                                    <code className="flex-1 font-mono text-lg font-bold text-amber-900 bg-white px-3 py-2 rounded-lg border border-amber-200">
                                                        {revealedPassword.password}
                                                    </code>
                                                    <CopyButton text={revealedPassword.password} />
                                                </div>
                                            </div>
                                            <button
                                                onClick={() => setPasswordModal(prev => ({ ...prev, mode: 'change' }))}
                                                className="w-full mt-3 py-2.5 border border-blue-200 text-blue-600 hover:bg-blue-50 rounded-xl text-sm font-medium transition-colors flex items-center justify-center gap-2"
                                            >
                                                <Lock className="w-4 h-4" /> Set Custom Password Instead
                                            </button>
                                        </div>
                                    )}

                                    {/* Change mode — set custom password */}
                                    {passwordModal.mode === 'change' && (
                                        <div>
                                            <p className="text-xs text-gray-500 mb-3">
                                                Set a new password for <strong>{passwordModal.userName}</strong>. Minimum 6 characters.
                                            </p>
                                            <div className="relative">
                                                <input
                                                    type={showNewPassword ? 'text' : 'password'}
                                                    placeholder="Enter new password..."
                                                    value={newPassword}
                                                    onChange={(e) => setNewPassword(e.target.value)}
                                                    onKeyDown={(e) => e.key === 'Enter' && handleChangePassword()}
                                                    className="w-full px-4 py-3 pr-12 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100"
                                                    autoFocus
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() => setShowNewPassword(!showNewPassword)}
                                                    className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600"
                                                >
                                                    {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                                </button>
                                            </div>
                                            {newPassword.length > 0 && newPassword.length < 6 && (
                                                <p className="text-xs text-red-500 mt-1">Password must be at least 6 characters</p>
                                            )}
                                            <button
                                                onClick={handleChangePassword}
                                                disabled={passwordLoading || newPassword.length < 6}
                                                className="w-full mt-3 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-xl text-sm font-bold transition-colors flex items-center justify-center gap-2"
                                            >
                                                {passwordLoading ? <Loader className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />}
                                                Set Password
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Edit User Role Modal */}
                    {editUserModal && (
                        <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4">
                            <div className="bg-white rounded-2xl shadow-2xl border border-purple-100 w-full max-w-sm overflow-visible">
                                <div className="p-5 bg-purple-50 rounded-t-2xl border-b border-purple-100 flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 rounded-full bg-purple-100 text-purple-600 flex items-center justify-center">
                                            <Shield className="w-5 h-5" />
                                        </div>
                                        <div>
                                            <h3 className="font-bold text-purple-900 text-sm">Edit User Role</h3>
                                            <p className="text-xs text-purple-600">{editUserModal.username || editUserModal.email}</p>
                                        </div>
                                    </div>
                                    <button onClick={() => setEditUserModal(null)} className="p-1.5 hover:bg-purple-100 rounded-lg">
                                        <X className="w-4 h-4 text-purple-600" />
                                    </button>
                                </div>
                                <div className="p-5 pb-40 space-y-4">
                                    <div>
                                        <label className="block text-xs font-bold text-purple-700 mb-2">Role</label>
                                        <Dropdown>
                                            <DropdownTrigger>
                                                <button type="button" className="w-full px-3 py-2.5 border rounded-lg text-sm bg-white flex justify-between items-center text-left">
                                                    {editUserModal.role}
                                                    <ChevronDown className="w-4 h-4 text-gray-400 shrink-0 ml-2" />
                                                </button>
                                            </DropdownTrigger>
                                            <DropdownPopover className="w-full">
                                                <DropdownMenu
                                                    selectedKeys={new Set([editUserModal.role])}
                                                    selectionMode="single"
                                                    onSelectionChange={(keys) => setEditUserModal({ ...editUserModal, role: Array.from(keys)[0] })}
                                                >
                                                    {ROLES.map((role) => (
                                                        <DropdownItem key={role} id={role}>{role}</DropdownItem>
                                                    ))}
                                                </DropdownMenu>
                                            </DropdownPopover>
                                        </Dropdown>
                                    </div>
                                    <button
                                        onClick={handleUpdateUserRole}
                                        disabled={editUserLoading}
                                        className="w-full mt-3 py-2.5 bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white rounded-xl text-sm font-bold transition-colors flex items-center justify-center gap-2"
                                    >
                                        {editUserLoading ? <Loader className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
                                        Save Changes
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
